import React, { useState, useEffect, useCallback } from "react";
import apiService from "../services/apiService";
import { handleDeleteError, handleUpdateError, handleCreateError, handleToggleError } from "../utils/errorHandler";
import {
  Button,
  Container,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Table,
  Box,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Menu,
  Switch,
  Popover,
  FormControl,
  Autocomplete,
  Paper,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlined from "@mui/icons-material/EditOutlined";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Table_theme from "../Themes/TableTheme";
import theme from "../Themes/FormThemes";
import { ReactComponent as DeleteIcon } from "../Icons/Delete.svg";
import { ReactComponent as SettingsIcon } from "../Icons/Settings.svg";
import FilterListIcon from "@mui/icons-material/FilterList";
import { ReactComponent as FlagIcon } from "../Icons/Flag.svg";

const Applications = () => {
  const [clients, setClients] = useState([]);
  const [environments, setEnvironments] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState({
    cl_id: "",
    env_id: "",
    app_id: "",
    name: "",
    created_by: "",
  });
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false); // Add loading state
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteData, setDeleteData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [visibleColumns, setVisibleColumns] = useState({
    client_name: true,
    environment_name: true,
    app_id: true,
    name: true,
  });
  const [filters, setFilters] = useState({
    client_name: "",
    environment_name: "",
    app_id: "",
    name: "",
  });
  const [filterAnchorEls, setFilterAnchorEls] = useState({});
  const [tempFilters, setTempFilters] = useState({ ...filters });
  const [searchTerm, setSearchTerm] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [originalFormData, setOriginalFormData] = useState({});
  const [isFormChanged, setIsFormChanged] = useState(false);

  const handleFilterIconClick = (event, column) => {
    setFilterAnchorEls((prev) => ({ ...prev, [column]: event.currentTarget }));
  };

  const handleFilterClose = (column) => {
    setFilterAnchorEls((prev) => ({ ...prev, [column]: null }));
  };

  const applyFilter = (column) => {
    setFilters((prev) => ({ ...prev, [column]: tempFilters[column] }));
    handleFilterClose(column);
    setPage(1);
  };

  const clearFilter = (column) => {
    setTempFilters((prev) => ({ ...prev, [column]: "" }));
    setFilters((prev) => ({ ...prev, [column]: "" }));
    handleFilterClose(column);
    setPage(1);
  };

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const fetchClients = async () => {
    try {
      const data = await apiService.get("/clients");
      setClients(data);
    } catch (err) {
      console.error("Error fetching clients:", err);
    }
  };

  const fetchEnvironments = async () => {
    try {
      const data = await apiService.get("/environments");
      setEnvironments(data);
    } catch (err) {
      console.error("Error fetching environments:", err);
    }
  };

  const fetchApplications = useCallback(async () => {
    setLoading(true); // Start loading
    const params = new URLSearchParams({ page, limit: rowsPerPage });

    // Add search term if present
    if (searchTerm.trim() !== "") {
      params.append("search", searchTerm.trim());
    }

    Object.entries(filters).forEach(([key, value]) => {
      if (value.trim() !== "") {
        params.append(key, value);
      }
    });

    try {
      const result = await apiService.get(
        `/applications/paginate/list?${params.toString()}`,
      );
      setData(result.data || []);
      setTotal(result.total || 0);
    } catch (err) {
      setData([]);
      setTotal(0);
      console.error("Error fetching applications:", err);
    } finally {
      setLoading(false); // End loading
    }
  }, [page, rowsPerPage, filters, searchTerm]);

  useEffect(() => {
    fetchApplications();
    fetchClients();
    fetchEnvironments();
  }, [fetchApplications]);

  // Pre-select first client when clients load
  useEffect(() => {
    if (clients.length > 0 && !formData.cl_id) {
      setFormData((prev) => ({
        ...prev,
        cl_id: clients[0].cl_id,
        client_name: clients[0].name,
      }));
    }
  }, [clients, formData.cl_id]);

  // Track form changes for Update button
  useEffect(() => {
    if (isEditing && Object.keys(originalFormData).length > 0) {
      const hasChanged =
        JSON.stringify(formData) !== JSON.stringify(originalFormData);
      setIsFormChanged(hasChanged);
    }
  }, [formData, originalFormData, isEditing]);

  const handleOpenDialog = (app = null) => {
    if (app) {
      const appData = {
        cl_id: app.cl_id || "",
        client_name: app.client_name || "",
        env_id: app.env_id || "",
        app_id: app.app_id || "",
        name: app.name || "",
        created_by: app.created_by || "",
      };
      setFormData(appData);
      setOriginalFormData(appData);
      setIsEditing(true);
      setIsFormChanged(false);
    } else {
      // Pre-select first client
      const defaultClient = clients.length > 0 ? clients[0].cl_id : "";
      const defaultClientName = clients.length > 0 ? clients[0].name : "";

      setFormData({
        cl_id: defaultClient,
        client_name: defaultClientName,
        env_id: "",
        app_id: "",
        name: "",
        created_by: "",
      });
      setOriginalFormData({});
      setIsEditing(false);
      setIsFormChanged(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setOriginalFormData({});
    setIsFormChanged(false);
    setFormData({
      cl_id: "",
      env_id: "",
      app_id: "",
      name: "",
      created_by: "",
    });
    setFormErrors({});
  };

  const handleSave = async () => {
    const errors = {};
    const onlySpecialCharRegex = /^[^a-zA-Z0-9]+$/; // Only special characters
    const onlyNumbersRegex = /^[0-9]+$/; // Only numbers
    const noLettersRegex = /^[^a-zA-Z]+$/; // No letters (only numbers and/or special chars)

    // Environment validation
    if (!formData.env_id) {
      errors.env_id = "Environment is required";
    }

    // Application ID validation
    const trimmedAppId = formData.app_id.trim();
    if (!trimmedAppId) {
      errors.app_id = "Application ID is required";
    } else if (onlySpecialCharRegex.test(trimmedAppId)) {
      errors.app_id = "Application ID cannot contain only special characters";
    } else if (onlyNumbersRegex.test(trimmedAppId)) {
      errors.app_id = "Application ID cannot contain only numbers";
    } else if (noLettersRegex.test(trimmedAppId)) {
      errors.app_id = "Application ID must contain at least one letter";
    } else if (!isEditing) {
      // Check for duplicate Application ID only when adding new
      const isDuplicate = data.some(
        (app) =>
          app.app_id.toLowerCase() === trimmedAppId.toLowerCase() &&
          app.env_id === formData.env_id,
      );
      if (isDuplicate) {
        errors.app_id = "Application ID already exists in this environment";
      }
    }

    // Name validation
    const trimmedName = formData.name.trim();
    if (!trimmedName) {
      errors.name = "Name is required";
    } else if (onlySpecialCharRegex.test(trimmedName)) {
      errors.name = "Name cannot contain only special characters";
    } else if (onlyNumbersRegex.test(trimmedName)) {
      errors.name = "Name cannot contain only numbers";
    } else if (noLettersRegex.test(trimmedName)) {
      errors.name = "Name must contain at least one letter";
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const payload = {
      cl_id: formData.cl_id,
      env_id: formData.env_id,
      app_id: trimmedAppId,
      name: trimmedName,
      created_by: formData.created_by,
    };

    try {
      if (isEditing) {
        await apiService.put(`/applications/${formData.app_id}`, payload);
      } else {
        await apiService.post("/applications", payload);
      }
      await fetchApplications();
      setSuccessMessage(
        isEditing
          ? "Application updated successfully!"
          : "Application created successfully!",
      );
      setTimeout(() => setSuccessMessage(""), 3000);
      handleCloseDialog();
    } catch (error) {
      console.error("Error saving application:", error);
      // Use centralized error handler
      const errorMessage = isEditing 
        ? handleUpdateError(error, "Application")
        : handleCreateError(error, "Application");
      setErrorMessage(errorMessage);
      setTimeout(() => setErrorMessage(""), 3000);
    }
  };

  const handleDelete = (app_id, env_id, cl_id) => {
    setDeleteData({ app_id, env_id, cl_id });
    setOpenDeleteDialog(true);
  };

  const handleToggleFlag = async (app_id, env_id, active_flag) => {
    try {
      if (!app_id) {
        throw new Error("Missing application ID");
      }
      const newFlag = active_flag === 1 ? 0 : 1;
      await apiService.put(`/applications/${app_id}`, {
        app_id,
        env_id,
        active_flag: newFlag,
      });
      await fetchApplications();

      // Show success message
      const message =
        newFlag === 1 ? "Application Activated" : "Application Deactivated";
      setSuccessMessage(message);
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("Error toggling active flag:", error);
      // Use centralized error handler
      const errorMessage = handleToggleError(error, "Application");
      setErrorMessage(errorMessage);
      setTimeout(() => setErrorMessage(""), 3000);
    }
  };

  const columns = ["client_name", "environment_name", "app_id", "name"];
  const columnLabels = {
    client_name: "Client",
    environment_name: "Environment",
    app_id: "Application ID",
    name: "Application",
  };

  // Calculate the number of visible columns for colSpan
  const visibleColumnCount =
    Object.values(visibleColumns).filter(Boolean).length + 1; // +1 for Actions column

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{ width: "100%", maxWidth: "none" }}
    >
      {/* Header: Title and Action Buttons on the SAME line */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        {/* Applications Title */}
        <Typography
          variant="h4"
          fontSize="25px"
          fontWeight="600"
          color="#454545"
          sx={{ mb: 0 }}
        >
          Applications
        </Typography>

        {/* Right side: Buttons + Pagination all on the same line */}
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          justifyContent="space-between"
          sx={{ mb: 0 }}
        >
          {/* Action Buttons Group */}
          <Box display="flex" alignItems="center" gap={0.25} flexWrap="nowrap">
            {/* Search Input */}
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1); // Reset to first page when searching
              }}
              sx={{
                width: "200px",
                mr: 1,
                "& .MuiInputBase-root": {
                  height: 35,
                },
                "& .MuiInputBase-input": {
                  fontSize: "0.75rem",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.75rem",
                },
              }}
              InputLabelProps={{ shrink: true }}
            />

            {/* Add + */}
            <Button
              onClick={() => handleOpenDialog(null)}
              sx={{
                color: "#666",
                fontWeight: "bold",
                borderRadius: "8px",
                height: "35px",
                minWidth: "50px",
                px: 1.5,
                textTransform: "none",
                fontSize: "12px",
                "&:hover": {
                  color: "#1976d2",
                },
              }}
            >
              Add +
            </Button>

            {/* Settings */}
            <Tooltip title="Column Customization" arrow>
              <IconButton
                onClick={handleOpen}
                sx={{
                  color: "#666",
                  height: "35px",
                  width: "30px",
                  borderRadius: "8px",
                  "&:hover": {
                    color: "#1976d2",
                  },
                }}
              >
                <SettingsIcon style={{ width: 17, height: 17 }} />
              </IconButton>
            </Tooltip>

            {/* Left Arrow - Smaller button */}
            <IconButton
              onClick={() => {
                if (page > 1) setPage(page - 1);
              }}
              disabled={page <= 1}
              size="small"
              sx={{
                width: "30px",
                height: "35px",
                borderRadius: "6px",
                color: "#666",
                "&:hover": {
                  color: "#1976d2",
                },
                "&:disabled": {
                  color: "#ccc",
                },
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
              </svg>
            </IconButton>

            {/* Compact Pagination Text */}
            <Box
              sx={{
                fontSize: "13px",
                fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
                whiteSpace: "nowrap",
                fontWeight: 500,
              }}
            >
              {`${(page - 1) * rowsPerPage + 1}-${Math.min(
                page * rowsPerPage,
                total,
              )} of ${total}`}
            </Box>

            {/* Right Arrow - Smaller button */}
            <IconButton
              onClick={() => {
                const totalPages = Math.ceil(total / rowsPerPage);
                if (page < totalPages) setPage(page + 1);
              }}
              disabled={page >= Math.ceil(total / rowsPerPage)}
              size="small"
              sx={{
                width: "30px",
                height: "35px",
                borderRadius: "6px",
                color: "#666",
                "&:hover": {
                  color: "#1976d2",
                },
                "&:disabled": {
                  color: "#ccc",
                },
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
              </svg>
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Alerts (success/error) - placed right below the header */}
      {successMessage && <Alert severity="success">{successMessage}</Alert>}
      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 1,
            p: 1,
            width: 200,
            maxHeight: 300,
            overflowY: "auto",
            borderRadius: 2,
          },
        }}
      >
        {Object.keys(visibleColumns).map((col) => (
          <MenuItem key={col} disableGutters sx={{ p: 0, mb: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                border: "1px solid #ccc",
                borderRadius: 1.5,
                px: 2,
                py: 1,
                backgroundColor: "#fff",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  color: "#333",
                  fontWeight: "Bold",
                }}
              >
                {columnLabels[col] || col}
              </Typography>
              <Switch
                size="small"
                checked={visibleColumns[col]}
                onChange={(e) =>
                  setVisibleColumns((prev) => ({
                    ...prev,
                    [col]: e.target.checked,
                  }))
                }
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#2DB0E4" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#2DB0E4",
                  },
                }}
              />
            </Box>
          </MenuItem>
        ))}
      </Menu>

      <Box
        sx={{
          height: "calc(90vh - 90px)",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <ThemeProvider theme={Table_theme}>
          <CssBaseline />
          <Paper elevation={3}>
            <TableContainer
              sx={{
                flexGrow: 1,
                maxHeight: "calc(100vh - 260px)",
                overflowY: "auto",
                overflowX: "auto",
              }}
            >
              <Table sx={{ minWidth: 650 }}>
                <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableRow>
                    <TableCell>Actions</TableCell>
                    {columns.map(
                      (col) =>
                        visibleColumns[col] && (
                          <TableCell key={col}>
                            {columnLabels[col] || col}
                            <IconButton
                              onClick={(e) => handleFilterIconClick(e, col)}
                            >
                              <FilterListIcon fontSize="small" />
                            </IconButton>
                            <Popover
                              open={Boolean(filterAnchorEls[col])}
                              anchorEl={filterAnchorEls[col]}
                              onClose={() => handleFilterClose(col)}
                              anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "left",
                              }}
                            >
                              <DialogContent>
                                <TextField
                                  label={`Filter ${columnLabels[col]}`}
                                  value={tempFilters[col] || ""}
                                  onChange={(e) =>
                                    setTempFilters((prev) => ({
                                      ...prev,
                                      [col]: e.target.value,
                                    }))
                                    
                                  }
                                  fullWidth
                                />
                                <DialogActions>
                                  <Button onClick={() => clearFilter(col)}>
                                    Clear
                                  </Button>
                                  <Button
                                    onClick={() => applyFilter(col)}
                                    variant="contained"
                                  >
                                    Filter
                                  </Button>
                                </DialogActions>
                              </DialogContent>
                            </Popover>
                          </TableCell>
                        ),
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell 
                        colSpan={visibleColumnCount} 
                        sx={{ 
                          position: "sticky",
                          left: 0,
                          textAlign: "left", 
                          padding: "20px",
                          backgroundColor: "white",
                          zIndex: 1
                        }}
                      >
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : data.length > 0 ? (
                    data.map((app) => (
                      <TableRow
                        key={`${app.cl_id}-${app.env_id}-${app.app_id}`}
                      >
                        <TableCell>
                          <Box
                            display="flex"
                            gap={1}
                            alignItems="center"
                            justifyContent="flex-start"
                          >
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleToggleFlag(
                                  app.app_id,
                                  app.env_id,
                                  parseInt(app.active_flag),
                                )
                              }
                              title="Toggle Active Flag"
                              sx={{
                                "&:hover": {
                                  backgroundColor: "rgba(0,0,0,0.04)",
                                },
                              }}
                            >
                              <FlagIcon
                                style={{
                                  color:
                                    parseInt(app.active_flag) === 1
                                      ? "#00CC22"
                                      : "#FF0C0C",
                                  transition: "color 0.2s",
                                  width: 17,
                                  height: 17,
                                  verticalAlign: "middle",
                                }}
                              />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog(app)}
                              title="Edit"
                              sx={{
                                color: "#2DB0E4",
                                "&:hover": { color: "#1976d2" },
                              }}
                            >
                              <EditOutlined sx={{ fontSize: "16px" }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleDelete(app.app_id, app.env_id, app.cl_id)
                              }
                              title="Delete"
                              sx={{
                                color: "#666",
                                "&:hover": { color: "#d32f2f" },
                              }}
                            >
                              <DeleteIcon
                                style={{
                                  width: 12,
                                  height: 15,
                                  cursor: "pointer",
                                }}
                              />
                            </IconButton>
                          </Box>
                        </TableCell>
                        {visibleColumns.client_name && (
                          <TableCell>{app.client_name || "N/A"}</TableCell>
                        )}
                        {visibleColumns.environment_name && (
                          <TableCell>{app.environment_name || "N/A"}</TableCell>
                        )}
                        {visibleColumns.app_id && (
                          <TableCell>{app.app_id || "N/A"}</TableCell>
                        )}
                        {visibleColumns.name && (
                          <TableCell>{app.name || "N/A"}</TableCell>
                        )}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell 
                        colSpan={visibleColumnCount} 
                        sx={{ 
                          position: "sticky",
                          left: 0,
                          textAlign: "left", 
                          padding: "20px",
                          backgroundColor: "white",
                          zIndex: 1
                        }}
                      >
                        No matching data found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </ThemeProvider>

        <ThemeProvider theme={theme}>
          <CssBaseline />

          <Dialog
            open={openDialog}
            onClose={(event, reason) => {
              if (reason === "backdropClick" || reason === "escapeKeyDown") {
                return;
              }
              handleCloseDialog();
            }}
            disableEscapeKeyDown
            maxWidth="md"
          >
            <DialogTitle
              sx={{
                fontWeight: "700",
                fontSize: "16px",
                py: 1,
              }}
            >
              {isEditing ? "Edit Application" : "Add Application"}
            </DialogTitle>
            <IconButton
              aria-label="close"
              onClick={handleCloseDialog}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
            <DialogContent dividers sx={{ p: 1.1 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: 1,
                }}
              >
                {/* Frozen Client Field - Read-only with gray background */}
                <TextField
                  fullWidth
                  label="Client"
                  required
                  value={
                    formData.client_name ||
                    clients.find((c) => c.cl_id === formData.cl_id)?.name ||
                    ""
                  }
                  InputProps={{
                    readOnly: true,
                  }}
                 
                  sx={{
                     minWidth: 300,
                    backgroundColor: "white",
                    "& .MuiInputBase-input": {
                      cursor: "not-allowed",
                      color: "grey",
                    },
                  }}
                  margin="dense"
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                <FormControl
                  fullWidth
                  margin="none"
                  error={!!formErrors.env_id}
                >
                  <Autocomplete
                    id="env_id"
                    options={environments}
                    getOptionLabel={(option) => option.name || ""}
                    value={
                      environments.find(
                        (environment) => environment.env_id === formData.env_id,
                      ) || null
                    }
                    onChange={(event, newValue) => {
                      setFormData((prev) => ({
                        ...prev,
                        env_id: newValue ? newValue.env_id : "",
                      }));
                      setFormErrors((prev) => ({ ...prev, env_id: "" }));
                    }}
                    ListboxProps={{
                      sx: {
                        "& .MuiAutocomplete-option": {
                          fontSize: "0.875rem",
                          minHeight: "32px",
                        },
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Environment"
                        error={!!formErrors.env_id}
                        required
                        helperText={formErrors.env_id}
                        sx={{
                     minWidth: 300,
                    backgroundColor: "white",
                    "& .MuiInputBase-input": {
                      cursor: "not-allowed",
                      color: "grey",
                    },
                  }}
                      />
                    )}
                    disabled={environments.length === 0}
                  />
                </FormControl>
                <TextField
                  label="Application ID"
                  fullWidth
                  margin="dense"
                  value={formData.app_id}
                  required
                  onChange={(e) =>
                    setFormData({ ...formData, app_id: e.target.value })
                  }
                  error={!!formErrors.app_id}
                  helperText={formErrors.app_id}
                  disabled={isEditing}
                  sx={{
                     minWidth: 300,
                   
                  }}
                />
                <TextField
                  label="Name"
                  fullWidth
                  margin="dense"
                  value={formData.name}
                  required
                  sx={{ minWidth: 140 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog}>Cancel</Button>

              <Button
                onClick={handleSave}
                variant="contained"
                disabled={isEditing && !isFormChanged}
              >
                {isEditing ? "Update" : "Add"}
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={openDeleteDialog}
            onClose={() => {
              setOpenDeleteDialog(false);
              setDeleteData(null);
            }}
          >
            <DialogTitle sx={{ fontWeight: "bold" }}>
              Confirm Delete
            </DialogTitle>

            <DialogContent>
              <Typography fontSize="0.9rem">
                Are you sure you want to delete Application{" "}
                <strong>
                  {
                    data.find(
                      (app) =>
                        app.app_id === deleteData?.app_id &&
                        app.env_id === deleteData?.env_id,
                    )?.name
                  }
                </strong>{" "}
                ?
              </Typography>
            </DialogContent>

            <DialogActions>
              <Button
                onClick={() => {
                  setOpenDeleteDialog(false);
                  setDeleteData(null);
                }}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                onClick={async () => {
                  try {
                    await apiService.delete(
                      `/applications/${deleteData.app_id}?env_id=${deleteData.env_id}`,
                    );

                    setSuccessMessage("Application deleted successfully!");
                    setTimeout(() => setSuccessMessage(""), 3000);

                    fetchApplications();
                  } catch (err) {
                    // Use centralized error handler
                    const errorMessage = handleDeleteError(err, "Application");
                    setErrorMessage(errorMessage);
                    setTimeout(() => setErrorMessage(""), 5000);
                  } finally {
                    setOpenDeleteDialog(false);
                    setDeleteData(null);
                  }
                }}
              >
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
        </ThemeProvider>
      </Box>
    </Container>
  );
};

export default Applications;
